import apCaseOnlyTitleTags from '@utils/apCaseOnlyTitleTags';
import paddingDefault from '@utils/paddingDefault';
import RichTextEditorStyles from '@utils/RichTextEditorStyles';
import { graphql } from 'gatsby';
import React from 'react';
import Container from '../../primitives/grid/container';
import FnolForm from './fnol';
import FranchiseOwnershipForm from './franchise-ownership';
import FirstResponderForm from './first-responder';
import CovidCleaningForm from './covid-cleaning';
import CeForm from './continuing-education';
import PreferredVendorForm from './preferred-vendor';
import FeedbackForm from './feedback-form';
import { colors } from '../../primitives/colors';
import RoofingForm from './roofing';

interface FormBuilderProps {
    successMessage?: string
    formId: string
    submitButton?: string
}

export const FormBuilder = ({ successMessage, formId, submitButton }: FormBuilderProps): JSX.Element => {
    switch (formId) {
        case 'roofing':
            return (
                <RoofingForm
                    successMessage={successMessage}
                    submitButton={submitButton}
                />
            );
        case 'ce-form':
            return <CeForm submitButton={submitButton} />;
        case 'covid-cleaning-form':
            return (
                <CovidCleaningForm
                    successMessage={successMessage}
                    submitButton={submitButton}
                />
            );
        case 'first-responder-form':
            return (
                <FirstResponderForm
                    successMessage={successMessage}
                    submitButton={submitButton}
                />
            );
        case 'preferred-vendor-form':
            return (
                <PreferredVendorForm
                    successMessage={successMessage}
                    submitButton={submitButton}
                />
            );
        case 'franchise-ownership-form':
            return (
                <FranchiseOwnershipForm
                    submitButtonText={submitButton}
                    successMessageText={successMessage}
                />
            );
        case 'fnol-form':
            return (
                <FnolForm submitButtonText={submitButton} successMessageText={successMessage} />
            );
        case 'feedback-form':
            return (
                <FeedbackForm submitButton={submitButton} successMessage={successMessage} />
            );
        default:
            return (
                <code>
                    Missing Form:
                    {formId}
                </code>
            );
    }
};

const Form = ({ data }): JSX.Element => {
    const {
        headline,
        copy,
        spacing,
        form_id,
        submit_button_label,
        thank_you_message,
        settings = {},
    } = data;

    const { hide_background } = settings;

    return (
        <div
            css={{
                ...paddingDefault(spacing),
                background: hide_background ? colors.shadesWhite : colors.grey100,
            }}
        >
            <Container>
                {headline && (
                    <h2
                        css={styles.headline(!!copy)}

                        dangerouslySetInnerHTML={{ __html: apCaseOnlyTitleTags(headline) }}
                    />
                )}
                {copy && (
                    <div
                        css={[RichTextEditorStyles, styles.copy]}
                        dangerouslySetInnerHTML={{ __html: copy }}
                    />
                )}
                <FormBuilder
                    successMessage={thank_you_message || ''}
                    formId={form_id}
                    submitButton={submit_button_label}
                />
            </Container>
        </div>
    );
};

const styles = {
    headline: (isCopy: boolean) => ({
        fontSize: '2.25rem',
        lineHeight: 1,
        '@media (min-width: 1024px)': {
            fontSize: '54px',
        },
    }),
    copy: {
        marginRight: 'auto',
        marginLeft: 'auto',
        marginBottom: '0.75rem',
        fontSize: '1.125rem',
        lineHeight: '1.75rem',
        whiteSpace: 'pre-line',
        '@media (min-width: 1024px)': {
            fontSize: '1.25rem',
            lineHeight: '1.75rem',
        },
    },
};

export const query = graphql`
  fragment FormData on CmsPageBuilderBlocks {
    form {
      headline
      copy
      form_id
      submit_button_label
      thank_you_message
      spacing {
        top_gutter
        bottom_gutter
      }
      settings {
        hide_background
      }
    }
  }
`;

export default Form;
